"use client"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { ReactNode } from "react"
import useIsModalOpen from "./hooks"

/**
 * Helper function to actually open the modal.
 *
 * Called by any component that wants to open the modal.
 */
export function openModal(queryParam: string) {
  const currentParams = new URLSearchParams(window.location.search)
  currentParams.set(queryParam, "true")

  // Update URL without page refresh
  const newUrl = `${window.location.pathname}${currentParams.toString() ? `?${currentParams.toString()}` : ""}`
  window.history.pushState({}, "", newUrl)
}

/**
 * Wrapper around Shadcn UI's Dialog component that uses a URL query param to control visibility.
 */
export default function Modal({
  title,
  children,
  footer,
  openParam,
  ref,
  className,
}: {
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  ref?: React.Ref<HTMLDivElement>

  className?: string

  /**
   * The URL query param that will be used to control the visibility of the modal
   */
  openParam: string
}) {
  const { isOpen, handleOpenChange } = useIsModalOpen(openParam)

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "max-w-full max-h-[100vh] sm:max-h-[90vh] sm:max-w-[425px]",
          className
        )}
        ref={ref}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {children}

        <DialogFooter>
          {footer}
          {/* Callers of this component can provide their own footer, but we can also provide a default one here. */}
          {footer === undefined && (
            <DialogClose asChild>
              <Button>Done</Button>
            </DialogClose>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
